import { FiBriefcase } from 'react-icons/fi';
import { IoIosRefresh } from 'react-icons/io';
import { FaPlus } from 'react-icons/fa6';
import { Outlet, useNavigate } from 'react-router-dom';
import ThreeColumnLayout from '../../layouts/ThreeColumnLayout';
import FilterSidebar from '../../organisms/FilterSidebar';
import FilterSkeleton from '../../atoms/FilterSkeleton';
import ATable from '../../organisms/ATable';
import TableSkeleton from '../../atoms/TableSkeleton';
import Loading from '../../ions/Loading';
import TextEdit from '../../molecules/TextEdit';
import PageFooter from '../../molecules/PageFooter';
import { formatDate } from '../../../utils/collateralFormatters';
import useLegalCasePageList from '../../../hooks/useLegalCasePageList';
import { POLE_OPTIONS, STATUS_OPTIONS, type LegalCaseListItem } from '../../../types/LegalCase';

const columns = [
    { id: 'externalCaseNumber', title: 'Processo', field: 'externalCaseNumber', canSort: true, width: '180px' },
    { id: 'caseType', title: 'Tipo', field: 'caseType', canSort: false, width: '150px' },
    { id: 'pole', title: 'Polo', field: 'pole', canSort: false, width: '90px', cssClass: 'text-center' },
    { id: 'creditorEntity', title: 'Entidade credora', field: 'creditorEntity', canSort: true, width: '200px' },
    { id: 'court', title: 'Foro / Comarca', field: 'court', canSort: false, width: '200px' },
    { id: 'status', title: 'Situação', field: 'status', canSort: false, width: '110px', cssClass: 'text-center' },
    { id: 'openedAt', title: 'Distribuição', field: 'openedAt', canSort: true, width: '110px', cssClass: 'text-center' },
];

const labelFor = (counts: Record<string, number>, options: { value: string; label: string }[]) =>
    options.map(o => ({ ...o, count: counts?.[o.value] || 0 }));

const LegalCasePageList = () => {
    const navigate = useNavigate();
    const {
        rows, total, page, setPage, size,
        isLoading, initialLoading, filterCounts,
        searchInput, setSearchInput, setSearch,
        sort, setSort,
        pole, setPole,
        caseType, setCaseType,
        status, setStatus,
        refresh, handleRowClick,
        apiInfo,
    } = useLegalCasePageList();

    const poleCounts = labelFor(filterCounts?.pole || {}, POLE_OPTIONS);
    const statusCounts = labelFor(filterCounts?.status || {}, STATUS_OPTIONS);
    const totalAll = Object.values(filterCounts?.pole || {}).reduce((a: number, b: any) => a + (b || 0), 0);

    const clearFilters = () => { setPole(''); setCaseType(''); setStatus(''); };

    const filterGroups = initialLoading ? [] : [
        {
            title: '',
            children: (
                <div className="space-y-4">
                    <button onClick={clearFilters}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!pole && !status && !caseType
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                        <div className="flex items-center justify-between">
                            <span>Todos</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{totalAll}</span>
                        </div>
                    </button>

                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">POLO</div>
                        <div className="space-y-1">
                            {poleCounts.map(o => (
                                <button key={o.value} onClick={() => setPole(pole === o.value ? '' : o.value)}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${pole === o.value
                                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                    <div className="flex items-center justify-between">
                                        <span>{o.label}</span>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">{o.count}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">SITUAÇÃO</div>
                        <div className="space-y-1">
                            {statusCounts.map(o => (
                                <button key={o.value} onClick={() => setStatus(status === o.value ? '' : o.value)}
                                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${status === o.value
                                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                    <div className="flex items-center justify-between">
                                        <span>{o.label}</span>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">{o.count}</span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            ),
        },
    ];

    const onSort = (field: string, order: 'ASC' | 'DESC') => {
        setSort(order === 'DESC' ? `-${field}` : field);
    };
    const currentField = sort.startsWith('-') ? sort.slice(1) : sort;
    const currentOrder: 'ASC' | 'DESC' = sort.startsWith('-') ? 'DESC' : 'ASC';

    return (
        <>
            {isLoading && <Loading />}

            <ThreeColumnLayout
                leftSidebarWidth="w-60"
                leftSidebarTitle={
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center space-x-2">
                            <FiBriefcase className="text-blue-600" size={18} />
                            <span>Recuperação</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            {apiInfo.version && <div>v{apiInfo.version}</div>}
                            <div className={`font-medium ${apiInfo.environment === 'prd' ? 'text-red-600' :
                                apiInfo.environment === 'hml' ? 'text-yellow-600' : 'text-green-600'}`}>
                                {(apiInfo.environment || '').toUpperCase()}
                            </div>
                        </div>
                    </div>
                }
                leftSidebar={
                    <FilterSidebar filterGroups={filterGroups}>
                        {initialLoading && <FilterSkeleton />}
                    </FilterSidebar>
                }
            >
                <div className="flex flex-col h-full bg-white dark:bg-slate-800 relative overflow-hidden">
                    <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800 z-10">
                        <div className="flex items-center justify-between">
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Casos jurídicos</h1>
                            <div className="flex items-center space-x-2">
                                <button onClick={refresh}
                                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    title="Atualizar lista">
                                    <IoIosRefresh size={18} />
                                </button>
                                <button onClick={() => navigate('/recovery/legal-cases/new')}
                                    className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                                    <FaPlus size={12} /> Novo
                                </button>
                                <div className="w-72">
                                    <TextEdit label="Buscar processo, entidade, foro..." value={searchInput}
                                        onChange={(e: any) => setSearchInput(e.target.value)}
                                        onKeyDown={(e: any) => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); } }} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-auto [&_table]:table-fixed [&_table]:w-full">
                        {initialLoading ? (
                            <TableSkeleton rows={12} />
                        ) : (
                            <ATable headers={columns} fieldSorted={currentField} orderSorted={currentOrder} onSort={onSort}>
                                {rows.length === 0 ? (
                                    <tr><td colSpan={columns.length} className="py-8 text-center text-gray-500 dark:text-gray-400">Nenhum caso cadastrado</td></tr>
                                ) : rows.map((row: LegalCaseListItem, index: number) => (
                                    <tr key={row.id}
                                        className={`group hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'}`}
                                        onClick={() => handleRowClick(row)}>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm font-mono text-blue-700 dark:text-blue-400">{row.externalCaseNumber || '—'}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-gray-300">{row.caseTypeFormatted}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                            <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300">
                                                {row.poleFormatted}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white truncate" title={row.creditorEntity}>{row.creditorEntity}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-gray-300">
                                            {row.court || '—'}{row.district ? ` • ${row.district}` : ''}
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                            <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${row.status === 'OPEN'
                                                ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                                                : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300'}`}>
                                                {row.statusFormatted}
                                            </span>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm text-gray-500 dark:text-gray-400 text-center">{row.openedAt ? formatDate(row.openedAt) : '—'}</td>
                                    </tr>
                                ))}
                            </ATable>
                        )}
                    </div>

                    <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                        <PageFooter currentPage={page - 1} totalPages={Math.ceil(total / size)} itemsPerPage={size} totalElements={total} onPageChange={(p: number) => setPage(p + 1)} />
                    </div>
                </div>
            </ThreeColumnLayout>

            <Outlet context={{ refresh }} />
        </>
    );
};

export default LegalCasePageList;
